import { useState } from "react";
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { storeName } from "../lib/labels";
import { useIsMobile } from "../lib/useMobile";
import { usePersistedState } from "../lib/useSort";
import PeriodBadge from "./PeriodBadge";
const OUTLET_COLORS = ["#0a0a0a", "#c72c2c", "#6b6b6b", "#a37e2c", "#2c6b64"];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Convert an ISO week id ("2026-W37") to the Monday of that week
function isoWeekToMonday(id) {
  const [y, w] = id.split("-W").map(Number);
  const simple = new Date(Date.UTC(y, 0, 1 + (w - 1) * 7));
  const dow = simple.getUTCDay() || 7;
  if (dow <= 4) simple.setUTCDate(simple.getUTCDate() - dow + 1);
  else simple.setUTCDate(simple.getUTCDate() + 8 - dow);
  return simple;
}

// Format bucket key for X-axis (no year)
function bucketTick(bucket) {
  if (!bucket) return "";
  if (bucket.length === 10) {
    // YYYY-MM-DD → "Sep 8"
    const m = parseInt(bucket.slice(5, 7), 10);
    const d = parseInt(bucket.slice(8, 10), 10);
    return `${MONTHS[m - 1]} ${d}`;
  }
  if (bucket.includes("-W")) {
    // Week → Monday date without year
    const d = isoWeekToMonday(bucket);
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  }
  if (bucket.length === 7) {
    // YYYY-MM → "Sep"
    const m = parseInt(bucket.slice(5, 7), 10);
    return MONTHS[m - 1];
  }
  if (bucket.includes("-Q")) {
    // 2026-Q3 → "Q3"
    return bucket.split("-")[1];
  }
  return bucket; // year, e.g. "2026"
}

// Determine if the last data bucket represents an in-progress period
const COMPARE_OPTIONS = [
  { k: "prior", label: "Prior period", title: "The same number of buckets immediately before each one" },
  { k: "yoy", label: "Same period last year", title: "52 weeks back, so weekdays line up" },
  { k: "none", label: "Hidden", title: "Hide the comparison ghosts" },
];

export default function TrendChart({ data, granularity = "Month", loading = false }) {
  const [compare, setCompare] = usePersistedState("bm-trend:compare", "prior"); // prior | yoy | none
  const [focus, setFocus] = useState(null); // phone: single store in view, null = all
  const mobile = useIsMobile();
  if (!data?.length) return <Empty loading={loading} />;

  const allOutlets = Object.keys(data[0]).filter(
    (k) => !["bucket", "prior_bucket", "yoy_bucket", "month"].includes(k)
       && !k.endsWith("_prior") && !k.endsWith("_yoy")
  );
  const colorOf = (name) => OUTLET_COLORS[allOutlets.indexOf(name) % OUTLET_COLORS.length];
  const outlets = mobile && focus && allOutlets.includes(focus) ? [focus] : allOutlets;
  const hasPrior = data.some((r) => outlets.some((o) => r[`${o}_prior`] !== undefined));
  const hasYoy = data.some((r) => outlets.some((o) => r[`${o}_yoy`] !== undefined));
  const ghostSuffix = compare === "yoy" ? "_yoy" : "_prior";
  const ghostBucketKey = compare === "yoy" ? "yoy_bucket" : "prior_bucket";
  const ghostLabel = compare === "yoy" ? "last year" : "prior";
  const showGhost = compare !== "none" && (compare === "yoy" ? hasYoy : hasPrior);
  const lastBucket = data[data.length - 1]?.bucket;
  const tickStyle = { fontSize: mobile ? 10 : 11, fontFamily: "JetBrains Mono" };

  // Total chain sales ($) per bucket = sum of all store revenues in that period
  const chainTotal = (row) => allOutlets.reduce((s, o) => s + (Number(row[o]) || 0), 0);

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-4 md:p-6" data-testid="trend-chart">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="bm-eyebrow mb-1 text-neutral-500">{granularity.toLowerCase()}ly revenue by store</div>
          <h3 className="text-lg font-semibold">4 Store Trend</h3>
          <PeriodBadge label={`${bucketTick(data[0]?.bucket)} → ${bucketTick(lastBucket)} (current ${granularity.toLowerCase()} to date + 4 prior)`} note="uses the date picker's unit, always ends today" className="mt-1" />
        </div>
        <div className="flex items-start gap-6 flex-wrap">
          {(hasPrior || hasYoy) && (
            <div>
              <div className="bm-eyebrow mb-2 text-neutral-500">Compare dashed ghosts to</div>
              <div className="flex border border-[#0a0a0a]" role="group" aria-label="Trend comparison" data-testid="trend-compare-toggle">
                {COMPARE_OPTIONS.map((o, i) => (
                  <button
                    key={o.k}
                    onClick={() => setCompare(o.k)}
                    data-testid={`trend-compare-${o.k}`}
                    title={o.title}
                    className={`px-2.5 py-1.5 bm-eyebrow ${i > 0 ? "border-l border-[#0a0a0a]" : ""} ${
                      compare === o.k ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/10"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phone: one store at a time (or all) */}
      {mobile && allOutlets.length > 1 && (
        <div className="flex gap-1.5 mb-3 overflow-x-auto -mx-1 px-1 pb-1" data-testid="trend-store-toggle">
          {[null, ...allOutlets].map((name) => {
            const on = (focus || null) === name;
            return (
              <button
                key={name || "all"}
                onClick={() => setFocus(name)}
                data-testid={`trend-store-toggle-${name ? storeName(name) : "all"}`}
                className={`shrink-0 px-3 min-h-[40px] bm-eyebrow border transition-colors ${on ? "bg-[#0a0a0a] text-[#f5f2eb] border-[#0a0a0a]" : "border-[#0a0a0a]/40"}`}
                style={!on && name ? { borderBottomColor: colorOf(name), borderBottomWidth: 3 } : undefined}
              >
                {name ? storeName(name) : "All"}
              </button>
            );
          })}
        </div>
      )}

      <ResponsiveContainer width="100%" height={mobile ? 240 : 420}>
        <ComposedChart data={data} margin={{ top: 10, right: mobile ? 4 : 20, left: mobile ? -12 : 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#0a0a0a20" vertical={!mobile} />
          <XAxis
            dataKey="bucket"
            stroke="#0a0a0a"
            tick={tickStyle}
            tickFormatter={(b) => (b === lastBucket ? `${bucketTick(b)} (current)` : bucketTick(b))}
            interval={mobile ? "preserveStartEnd" : 0}
            minTickGap={mobile ? 28 : 5}
          />
          <YAxis yAxisId="left" stroke="#0a0a0a" tick={tickStyle} tickCount={mobile ? 4 : undefined}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
          {/* Right axis for total chain sales bars (desktop only) */}
          {!mobile && (
            <YAxis
              yAxisId="chain"
              orientation="right"
              stroke="#0a0a0a55"
              tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#0a0a0a99" }}
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            />
          )}
          <Tooltip
            contentStyle={{ background: "#f5f2eb", border: "1px solid #0a0a0a", color: "#0a0a0a", fontSize: 12, fontFamily: "JetBrains Mono" }}
            labelFormatter={(label, payload) => {
              const priorBucket = showGhost ? payload?.[0]?.payload?.[ghostBucketKey] : null;
              const cur = label === lastBucket ? `${bucketTick(label)} (current)` : bucketTick(label);
              return priorBucket ? `${cur}   vs ${ghostLabel} · ${bucketTick(priorBucket)}` : cur;
            }}
            formatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          />
          <Legend
            content={() => (
              <div
                className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-2"
                style={{ fontSize: mobile ? 10 : 11, fontFamily: "JetBrains Mono", textTransform: "uppercase", letterSpacing: mobile ? "0.08em" : "0.15em" }}
                data-testid="trend-legend"
              >
                {outlets.map((name, i) => {
                  const c = colorOf(name);
                  return (
                    <span key={name} className="inline-flex items-center gap-2" data-testid={`trend-legend-item-${i}`}>
                      <svg width="26" height="12" aria-hidden="true">
                        <line x1="0" y1="4" x2="26" y2="4" stroke={c} strokeWidth="2" />
                        {showGhost && (
                          <line x1="0" y1="9" x2="26" y2="9" stroke={c} strokeWidth="1.5" strokeOpacity="0.5" strokeDasharray="4 3" />
                        )}
                      </svg>
                      <span style={{ color: "#0a0a0a" }}>{storeName(name)}</span>
                    </span>
                  );
                })}
              </div>
            )}
          />
          {/* Total chain sales (all 4 stores) as background bars, right axis — desktop only */}
          {!mobile && (
            <Bar
              yAxisId="chain"
              dataKey={chainTotal}
              name="Chain total"
              fill="#0a0a0a"
              fillOpacity={0.10}
              barSize={granularity === "Day" ? 6 : undefined}
              isAnimationActive={false}
              legendType="square"
            />
          )}

          {/* Prior-period ghost lines: same store colour, dashed, translucent, no dots */}
          {showGhost && outlets.map((name) => (
            <Line
              key={`${name}${ghostSuffix}`}
              yAxisId="left"
              type="linear"
              dataKey={`${name}${ghostSuffix}`}
              name={`${storeName(name)} · ${ghostLabel}`}
              stroke={colorOf(name)}
              strokeOpacity={0.35}
              strokeWidth={1.5}
              strokeDasharray="4 4"
              dot={false}
              activeDot={false}
              legendType="plainline"
              isAnimationActive={false}
            />
          ))}

          {/* Current period — solid lines with dots */}
          {outlets.map((name) => (
            <Line
              key={name}
              yAxisId="left"
              type="linear"
              dataKey={name}
              name={storeName(name)}
              stroke={colorOf(name)}
              strokeWidth={2}
              dot={{ r: mobile ? 2.5 : 3, fill: colorOf(name), strokeWidth: 0 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>

      <div className="mt-3 bm-eyebrow text-neutral-500">
        Bucket: <span className="text-[#0a0a0a]">{granularity}</span>
        {!mobile && <> &middot; grey bars: <span className="text-[#0a0a0a]">total chain sales</span> (right axis)</>}
        {showGhost && <> &middot; {compare === "yoy" ? "same period last year (weekday-aligned)" : "prior period"} as dashed ghosts</>}
      </div>
    </div>
  );
}

function Empty({ loading = false }) {
  if (loading) {
    return (
      <div className="border border-[#0a0a0a] bg-[#ede8dc] p-10 text-center animate-pulse" data-testid="trend-chart-loading">
        <div className="bm-eyebrow text-neutral-600">Loading trend…</div>
      </div>
    );
  }
  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-10 text-center" data-testid="trend-chart-empty">
      <div className="bm-eyebrow">No trend data yet</div>
      <div className="mt-2 text-sm text-neutral-600">Run a sync to populate the four-store curve.</div>
    </div>
  );
}
